import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { Ban, CircleCheck, Eye, Hourglass, Inbox, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button, buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatCount, formatTaka } from '@/utils/number';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import { pcmpRequestedQueryOptions } from '../../api/pcmpRequested.api';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import { PCMP_REQUESTED_EMPTY_MESSAGE } from '../../constants/pcmpRequested.constants';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import type { PcmpQueueFilterController } from '../../hooks/usePcmpQueueFilters';
import { usePcmpRowSelection } from '../../hooks/usePcmpRowSelection';
import {
  PCMP_REQUESTED_APPROVED_TAB,
  PCMP_REQUESTED_PRE_BATCH_TAB,
  type PcmpRequestDecision,
  type PcmpRequestedItem,
  type PcmpRequestedParams,
  type PcmpRequestedTab,
} from '../../types/pcmpRequested.types';
import { PcmpHoldActionButtons } from '../PcmpHoldActions/PcmpHoldActionButtons';
import { PcmpSelectionBar } from '../PcmpBatch/PcmpSelectionBar';
import { pcmpSelectColumn } from '../PcmpQueue/pcmpSelectColumn';
import {
  PcmpUrgentStageDialog,
  type PcmpUrgentStageTarget,
} from '../PcmpUrgent/PcmpUrgentStageDialog';
import { PcmpDivisionTotals } from './PcmpDivisionTotals';
import { PcmpRequestActButtons } from './PcmpRequestActButtons';
import { PcmpRequestActDialog, type PcmpRequestActTarget } from './PcmpRequestActDialog';
import { getPcmpRequestedColumns } from './PcmpRequestedColumns';

interface PcmpRequestedListViewProps {
  /** The page's filters and paging: Requested Proposals, or the Accounts queue's Return Requests tab. */
  controller: Pick<PcmpQueueFilterController, 'limit' | 'appliedFilters' | 'setPage' | 'setLimit'>;
  params: PcmpRequestedParams;
  /** The current tab's entry from the counts, for its select-all amount. */
  tabInfo: PcmpRequestedTab | undefined;
}

/** One toast id, so each tick replaces the last count instead of stacking. */
const SELECTION_TOAST_ID = 'pcmp-requested-selection';

const BULK_BUTTONS = [
  { decision: 'PROCESS', label: 'Process', icon: Hourglass },
  { decision: 'APPROVE', label: 'Approve', icon: CircleCheck },
  { decision: 'REJECT', label: 'Reject', icon: Ban },
] as const satisfies readonly { decision: PcmpRequestDecision; label: string; icon: unknown }[];

/**
 * One Requested Proposals tab. On the open-request tabs an approver picks rows to
 * process, approve or reject; on Pre-Batch they pick proposals to stage into an urgent batch.
 */
export function PcmpRequestedListView({ controller, params, tabInfo }: PcmpRequestedListViewProps) {
  const router = useRouter();
  const { limit, appliedFilters } = controller;
  const { tab } = params;
  const { can } = usePcmpPermissions();
  // Admin, MIS, users granted Proposal Correction and Accounts staff (Accounts
  // Payment Return requests, LDS-805) decide requests; each row's canAct says
  // which. Only an approver stages urgent batches.
  const canDecide = can(PCMP_PERMISSION.REQUEST_ACT);
  const canStage = can(PCMP_PERMISSION.REQUEST_APPROVE, PCMP_PERMISSION.ACT);
  const isApprovedTab = tab === PCMP_REQUESTED_APPROVED_TAB;
  const isPreBatchTab = tab === PCMP_REQUESTED_PRE_BATCH_TAB;
  const canSelect = isPreBatchTab ? canStage : canDecide && !isApprovedTab;

  const selection = usePcmpRowSelection(JSON.stringify([tab, appliedFilters]));
  const [actTarget, setActTarget] = useState<PcmpRequestActTarget | null>(null);
  const [stageTarget, setStageTarget] = useState<PcmpUrgentStageTarget | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpRequestedQueryOptions(params),
  );

  useKeepPageInRange(params.page, data?.requests.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpRequestedItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const rows = useMemo(() => data?.requests.items ?? [], [data]);

  const columns = getPcmpRequestedColumns({
    leading: canSelect ? [pcmpSelectColumn<PcmpRequestedItem>({ selection })] : [],
    renderActions: (item) => (
      <>
        {isPreBatchTab || isApprovedTab ? null : (
          <PcmpRequestActButtons
            proposalId={item.proposalId}
            refNo={item.refNo}
            proposedAmount={item.proposedAmount}
            canAct={item.canAct}
            canProcess={item.canProcess}
            approveBlockedReason={item.approveBlockedReason}
            approveWarning={item.approveWarning}
            categoryCode={item.request.categoryCode}
          />
        )}
        <PcmpHoldActionButtons
          proposalId={item.proposalId}
          refNo={item.refNo}
          canHold={item.canHold}
          canRelease={item.canRelease}
          hold={item.hold}
          className="flex-nowrap"
        />
        <Link
          to="/pcmp/proposals/$proposalId"
          params={{ proposalId: item.proposalId }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
        >
          <Eye className="size-3.5" />
          View
        </Link>
      </>
    ),
  });

  const total = data?.requests.meta.total ?? 0;
  const tabAmount = data?.totalAmount ?? tabInfo?.amount ?? 0;
  const summary = selection.summarize(total, tabAmount);
  // Legacy LDS-800: a short alert with the running count and amount on every tick.
  const selectionKey = `${selection.allMatching}:${selection.pickedIds.join(',')}`;
  const lastSelectionKey = useRef(selectionKey);
  useEffect(() => {
    if (lastSelectionKey.current === selectionKey) return;
    lastSelectionKey.current = selectionKey;
    if (summary.count === 0) {
      toast.dismiss(SELECTION_TOAST_ID);
      return;
    }
    toast.info(
      `${formatCount(summary.count, isPreBatchTab ? 'proposal' : 'request')} selected · ${formatTaka(summary.amount)}`,
      { id: SELECTION_TOAST_ID, duration: 2500 },
    );
  }, [selectionKey, summary.count, summary.amount, isPreBatchTab]);

  const isPageSelected =
    rows.length > 0 && rows.every((row) => selection.isSelected(row.proposalId));

  const refNoOf = (proposalId: number) => rows.find((row) => row.proposalId === proposalId)?.refNo;

  const startAct = (decision: PcmpRequestDecision) =>
    setActTarget({
      decision,
      count: summary.count,
      amount: summary.amount,
      categoryCode: tab,
      run: {
        kind: 'bulk',
        selection: selection.allMatching
          ? {
              selectAllMatching: true,
              tab,
              filters: appliedFilters,
              excludedProposalIds: selection.pickedIds,
            }
          : { proposalIds: selection.pickedIds },
      },
    });

  const startStage = () =>
    setStageTarget({
      count: summary.count,
      amount: summary.amount,
      selection: selection.allMatching
        ? {
            selectAllMatching: true,
            filters: appliedFilters,
            excludedProposalIds: selection.pickedIds,
          }
        : { proposalIds: selection.pickedIds },
    });

  return (
    <>
      {data?.divisionTotals ? (
        <PcmpDivisionTotals divisionTotals={data.divisionTotals} regionTotals={data.regionTotals} />
      ) : null}

      {canSelect ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          selectAll={{ total, amount: tabAmount, isPageSelected }}
          hint={
            isPreBatchTab
              ? 'Tick proposals to stage them into an urgent batch.'
              : 'Tick requests to process, approve or reject them together.'
          }
        >
          {isPreBatchTab ? (
            <Button
              type="button"
              size="sm"
              onClick={startStage}
              disabled={summary.count === 0}
              className="gap-1.5"
            >
              <Layers className="size-3.5" />
              Stage urgent batch
            </Button>
          ) : (
            BULK_BUTTONS.map(({ decision, label, icon: Icon }) => (
              <Button
                key={decision}
                type="button"
                size="sm"
                variant={decision === 'APPROVE' ? 'default' : 'outline'}
                onClick={() => startAct(decision)}
                disabled={summary.count === 0}
                className={cn(
                  'gap-1.5',
                  decision === 'REJECT' && 'text-destructive hover:text-destructive',
                )}
              >
                <Icon className="size-3.5" />
                {label}
              </Button>
            ))
          )}
        </PcmpSelectionBar>
      ) : null}

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the requests"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage={
            isApprovedTab
              ? PCMP_REQUESTED_EMPTY_MESSAGE.APPROVED
              : isPreBatchTab
                ? PCMP_REQUESTED_EMPTY_MESSAGE.PRE_BATCH
                : PCMP_REQUESTED_EMPTY_MESSAGE.default
          }
          skeletonRowCount={Math.min(limit, 15)}
          getRowClassName={(item) =>
            canSelect && selection.isSelected(item.proposalId) ? 'bg-primary/5' : undefined
          }
          onRowClick={openProposal}
        />
      )}

      {data?.requests.meta && !error ? (
        <DataTablePagination
          page={data.requests.meta.page}
          totalPages={data.requests.meta.totalPages}
          total={data.requests.meta.total}
          limit={data.requests.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}

      {actTarget ? (
        <PcmpRequestActDialog
          target={actTarget}
          refNoOf={refNoOf}
          onActed={selection.clear}
          onClose={() => setActTarget(null)}
        />
      ) : null}
      {stageTarget ? (
        <PcmpUrgentStageDialog
          target={stageTarget}
          refNoOf={refNoOf}
          onStaged={selection.clear}
          onClose={() => setStageTarget(null)}
        />
      ) : null}
    </>
  );
}
